import React from 'react';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Textarea } from '@components/ui/textarea';
import { cn } from '@core/utils/cn';
import { Tooltip } from '../tooltip';

interface InputAdornments {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
}

export interface TextFieldProps {
    className?: string;
    label?: string;
    type: 'email' | 'text' | 'number' | 'password';
    value: string | number;
    error?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    multiline?: boolean;
    maxRows?: number;
    size?: 'small' | 'medium';
    suffix?: string;
    InputProps?: InputAdornments;
    InputLabelShrink?: boolean;
    onKeyUp?: React.KeyboardEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    tooltipText?: string;
    helperText?: boolean;
    onChange(value: string | number): void;
    onBlur?(
        event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void;
}

export const FieldLabel = ({
    htmlFor,
    label,
    required,
    tooltipText
}: {
    htmlFor?: string;
    label?: string;
    required?: boolean;
    tooltipText?: string;
}) => {
    if (!label) {
        return null;
    }
    return (
        <Label htmlFor={htmlFor} className="text-muted-foreground">
            {tooltipText ? (
                <Tooltip title={tooltipText}>
                    <span>{label}</span>
                </Tooltip>
            ) : (
                label
            )}
            {required && <span className="text-destructive">*</span>}
        </Label>
    );
};

export const FieldError = ({ error }: { error?: string }) =>
    error ? <p className="text-xs text-destructive">{error}</p> : null;

export const TextField = React.forwardRef(
    (props: TextFieldProps, ref: React.Ref<HTMLInputElement>) => {
        const { type = 'text', size = 'medium' } = props;
        const id = React.useId();
        const start = props.InputProps?.startAdornment;
        const end = props.suffix ?? props.InputProps?.endAdornment;

        const control = props.multiline ? (
            <div className="relative">
                <Textarea
                    id={id}
                    value={props.value}
                    rows={props.maxRows ?? 3}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    required={props.required}
                    aria-invalid={Boolean(props.error)}
                    className={cn(end && 'pr-12')}
                    onChange={(e) => props.onChange(e.target.value)}
                    onBlur={props.onBlur}
                />
                {end && (
                    <span className="absolute top-2 right-3 text-sm text-muted-foreground">
                        {end}
                    </span>
                )}
            </div>
        ) : (
            <div className="relative">
                {start && (
                    <span className="absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                        {start}
                    </span>
                )}
                <Input
                    id={id}
                    ref={ref}
                    type={type}
                    value={props.value}
                    placeholder={props.placeholder}
                    disabled={props.disabled}
                    required={props.required}
                    autoComplete="off"
                    aria-invalid={Boolean(props.error)}
                    className={cn(
                        size === 'small' && 'h-8',
                        start && 'pl-9',
                        end && 'pr-12'
                    )}
                    onChange={(e) => props.onChange(e.target.value)}
                    onBlur={props.onBlur}
                    onKeyUp={props.onKeyUp}
                    onKeyDown={props.onKeyDown}
                />
                {end && (
                    <span className="absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                        {end}
                    </span>
                )}
            </div>
        );

        return (
            <div
                className={cn('flex w-full flex-col gap-1.5', props.className)}
            >
                <FieldLabel
                    htmlFor={id}
                    label={props.label}
                    required={props.required}
                    tooltipText={props.tooltipText}
                />
                {control}
                <FieldError error={props.error} />
            </div>
        );
    }
);

export const EmptyTextField = React.forwardRef(
    (props: TextFieldProps, ref: React.Ref<HTMLInputElement>) => {
        const end = props.suffix ?? props.InputProps?.endAdornment;
        return (
            <div className={cn('relative', props.className)}>
                <Input
                    ref={ref}
                    type={props.type}
                    className={cn('h-8', end && 'pr-12')}
                    required={props.required}
                    aria-invalid={Boolean(props.error)}
                    disabled={props.disabled}
                    onChange={(e) => props.onChange(e.target.value)}
                    placeholder={props.placeholder}
                    value={props.value}
                />
                {end && (
                    <span className="absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                        {end}
                    </span>
                )}
            </div>
        );
    }
);
